/**
 * Date and wording helpers shared by the follow-up queue and its prompts.
 *
 * Days are India calendar dates (`YYYY-MM-DD`), the day boundary the
 * follow-up rules use, whatever the browser's own zone is.
 */

import type { TFunction } from "i18next";
import type { FollowUpReasonType } from "@/features/realtor/services/realtor-crm-api.service";
import { dayKey, INDIA_TIME_ZONE } from "../messages/message-format";

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

/** The search parameter that opens the queue in full (bell row, digest). */
export const FOLLOW_UPS_PARAM = "followUps";
export const FOLLOW_UPS_ALL = "all";
/** The queue's element id. */
export const FOLLOW_UPS_ANCHOR = "follow-ups";
/** Home, with the follow-ups queue opened in full. */
export const FOLLOW_UPS_QUEUE_PATH = `/dashboard?${FOLLOW_UPS_PARAM}=${FOLLOW_UPS_ALL}`;

/** Phone: a bottom sheet. Desktop: a centred dialog about 28rem wide. */
export const PROMPT_CONTENT_CLASS =
	"border-dashboard-border bg-dashboard-bg md:max-w-md max-md:top-auto max-md:bottom-0 max-md:translate-y-0 max-md:max-w-none max-md:rounded-t-xl";

/** The quick picks shared by Snooze and "When should you follow up next?". */
export const QUICK_PICKS = [
	{ id: "tomorrow", days: 1 },
	{ id: "threeDays", days: 3 },
	{ id: "nextWeek", days: 7 },
] as const;

export type QuickPickId = (typeof QUICK_PICKS)[number]["id"];

/** Today's India date, e.g. "2026-10-12". */
export function indiaToday(now: Date = new Date()): string {
	return dayKey(now);
}

/** The India date `days` after `date` (both `YYYY-MM-DD`). */
export function addDays(date: string, days: number): string {
	const at = new Date(`${date}T00:00:00Z`);
	at.setUTCDate(at.getUTCDate() + days);
	return at.toISOString().slice(0, 10);
}

/** 10:00 India time on an India date, as an ISO instant. */
export function tenAmIndia(date: string): string {
	return new Date(`${date}T10:00:00+05:30`).toISOString();
}

/** A short India date for display, e.g. "16 Oct". */
export function shortIndiaDate(date: string, locale: string): string {
	return new Date(`${date}T12:00:00+05:30`).toLocaleDateString(locale, {
		timeZone: INDIA_TIME_ZONE,
		day: "numeric",
		month: "short",
	});
}

/**
 * A queue action: 44px tall below the xl breakpoint, where the card's
 * secondary actions show only their icon; 36px tall and sized to its label
 * in the xl-and-wider row.
 */
const ACTION_SHAPE =
	"inline-flex h-11 items-center justify-center gap-2 rounded-lg px-4 text-sm xl:h-9 xl:px-3 [&>svg]:shrink-0 font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dashboard-accent/40 disabled:cursor-not-allowed disabled:opacity-60";
const PRIMARY_TONE =
	"bg-dashboard-accent text-dashboard-on-fill hover:bg-dashboard-accent/90";
const SECONDARY_TONE =
	"border border-dashboard-border bg-dashboard-paper text-dashboard-text hover:bg-dashboard-surface";
export const ACTION_PRIMARY = `${ACTION_SHAPE} w-full min-w-0 xl:w-auto ${PRIMARY_TONE}`;
export const ACTION_SECONDARY = `${ACTION_SHAPE} w-full max-xl:px-0 xl:w-auto ${SECONDARY_TONE}`;
/** A secondary action that is always full width. */
export const ACTION_SECONDARY_FULL = `${ACTION_SHAPE} w-full ${SECONDARY_TONE}`;
/** A secondary action sized to its label. */
export const ACTION_SECONDARY_COMPACT = `${ACTION_SHAPE} shrink-0 ${SECONDARY_TONE}`;

const KNOWN_REASONS: readonly FollowUpReasonType[] = [
	"reply_waiting",
	"reminder_due",
	"viewing_feedback",
	"gone_quiet",
	"listing_match",
];

/** A reason's label. A reason this build does not know reads generically. */
export function reasonLabel(type: string, t: TFunction): string {
	return KNOWN_REASONS.includes(type as FollowUpReasonType)
		? t(`realtor.followUps.reasons.${type}`)
		: t("realtor.followUps.reasons.other");
}

/**
 * How long a reason has been waiting, e.g. "2 days". A reminder due later
 * today has a negative wait, which reads "due today".
 */
export function waitingLabel(
	type: string,
	waitingMs: number,
	t: TFunction,
): string {
	if (waitingMs <= 0) {
		return type === "reminder_due"
			? t("realtor.followUps.waiting.dueToday")
			: t("realtor.followUps.waiting.justNow");
	}
	if (waitingMs < HOUR_MS) {
		return t("realtor.followUps.waiting.minutes", {
			count: Math.max(1, Math.floor(waitingMs / MINUTE_MS)),
		});
	}
	if (waitingMs < DAY_MS) {
		return t("realtor.followUps.waiting.hours", {
			count: Math.floor(waitingMs / HOUR_MS),
		});
	}
	return t("realtor.followUps.waiting.days", {
		count: Math.floor(waitingMs / DAY_MS),
	});
}
